import _ from 'lodash';
import { ChangeEvent, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as uuid from 'uuid';

import { VStack } from '@vivaahealth/design-system';

import * as api from '../../api';

import { constructAddress } from '../../utils/practice';
import { getBlankGuarantor } from '../Guarantor';
import { EditGuarantorModal } from './EditGuarantorModal';
import { EditGuarantorValues } from './EditGuarantorModal/schema';
import {
  ExistingGuarantorCard,
  RowDataProps,
  SomeoneElseCard,
} from './GuarantorRadioButtonCards';

interface SomeoneElseInfo {
  isSelected: boolean;
  value: string;
}

interface Props {
  saveFieldValue: (value: string) => void;
  updateFieldValue: (value: string) => void;
  value: api.Guarantor.PrevisitGuarantorVerificationFormFieldValue;
}

const GuarantorVerification = ({
  saveFieldValue,
  updateFieldValue,
  value,
}: Props) => {
  const { t } = useTranslation(['guarantorVerification', 'common']);
  const radioButtonOptionName = 'guarantor-verification-option';

  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [guarantors, setGuarantors] = useState<
    api.Guarantor.PrevisitGuarantor[]
  >(value.guarantors);
  const [selectedGuarantorId, setSelectedGuarantorId] = useState(
    value.selectedGuarantorId || value.guarantors[0].id,
  );
  const [someoneElseInfo, setSomeoneElseInfo] = useState<SomeoneElseInfo>({
    isSelected: value.isSomeoneElse || false,
    value: value.someoneElse || '',
  });

  const saveField = useCallback(async () => {
    const { isSelected: isSomeoneElse, value: someoneElse } = someoneElseInfo;

    const newValue: api.Guarantor.PrevisitGuarantorVerificationFormFieldValue =
      {
        ...value,
        ...{ guarantors },
        ...{ selectedGuarantorId },
        ...{ isSomeoneElse },
        ...(isSomeoneElse && someoneElse
          ? { someoneElse }
          : { someoneElse: '' }),
      };

    if (_.isEqual(value, newValue)) return;

    try {
      const stringifiedNewValue = JSON.stringify(newValue);
      await updateFieldValue(stringifiedNewValue);
      await saveFieldValue(stringifiedNewValue);
    } catch (err) {
      return;
    }
  }, [
    someoneElseInfo,
    value,
    guarantors,
    selectedGuarantorId,
    updateFieldValue,
    saveFieldValue,
  ]);

  useEffect(() => {
    saveField();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [guarantors, someoneElseInfo]);

  const saveEditedGuarantor = (newGuarantorValues: EditGuarantorValues) => {
    const {
      firstName,
      lastName,
      address,
      relationshipToPatient,
      homePhone = null,
      mobilePhone = null,
    } = newGuarantorValues;
    setGuarantors((prev) => {
      const [oldGuarantorValues, ...restGuarantors] = prev;
      const editedGuarantorValues = {
        ...oldGuarantorValues,
        firstName,
        lastName,
        address,
        relationshipToPatient,
        phoneNumberHome: homePhone,
        phoneNumberMobile: mobilePhone,
      };
      return [editedGuarantorValues, ...restGuarantors];
    });
    setIsEditModalOpen(false);
  };

  const updateAndSaveSomeoneElseValue = async (
    e: ChangeEvent<HTMLInputElement>,
  ) => {
    const { value } = e.target;
    setSomeoneElseInfo((prev) => ({
      ...prev,
      value,
    }));
  };

  const {
    id,
    firstName,
    lastName,
    phoneNumberMobile,
    phoneNumberHome,
    phoneNumberWork,
    address,
    relationshipToPatient,
  } = guarantors[0];

  const guarantorDataRows: RowDataProps[] = [
    {
      data: phoneNumberMobile || phoneNumberHome || phoneNumberWork || '',
      label: t('phone', { ns: 'common' }),
      type: 'phone',
    },
    {
      data: address ? constructAddress(address) : '',
      label: t('address', { ns: 'common' }),
      type: 'address',
    },
  ];

  const readableRelationshipToPatient = relationshipToPatient
    ? ` (${t(
        api.Previsit.relationshipOptionsMap[relationshipToPatient],
      ).toLowerCase()})`
    : '';

  const radioButtonPropsExistingGuarantor = {
    checked: !someoneElseInfo.isSelected,
    label: `${firstName} ${lastName}${readableRelationshipToPatient}`,
    name: radioButtonOptionName,
    onChange: () => {
      setSelectedGuarantorId(id);
      setSomeoneElseInfo((prev) => ({
        ...prev,
        isSelected: false,
        value: '',
      }));
    },
  };

  const radioButtonPropsSomeoneElse = {
    checked: someoneElseInfo.isSelected,
    label: t('someoneElse', { ns: 'common' }),
    name: radioButtonOptionName,
    onChange: () => {
      let newlyAddedGuarantor = guarantors.find(
        (guarantor) => guarantor.newlyAdded,
      );
      if (!newlyAddedGuarantor) {
        newlyAddedGuarantor = {
          ...getBlankGuarantor(),
          newlyAdded: true,
          id: uuid.v4(),
        };
        setGuarantors([...guarantors, newlyAddedGuarantor]);
      }
      setSelectedGuarantorId(newlyAddedGuarantor.id);
      setSomeoneElseInfo((prev) => ({
        ...prev,
        isSelected: true,
      }));
    },
  };

  return (
    <>
      <VStack gap={16}>
        <ExistingGuarantorCard
          openEditModal={() => setIsEditModalOpen(true)}
          radioButtonProps={radioButtonPropsExistingGuarantor}
          rowData={guarantorDataRows}
        />
        <SomeoneElseCard
          radioButtonProps={radioButtonPropsSomeoneElse}
          updateAndSaveSomeoneElseValue={updateAndSaveSomeoneElseValue}
          value={someoneElseInfo.value}
        />
      </VStack>
      {isEditModalOpen && (
        <EditGuarantorModal
          guarantor={guarantors[0]}
          onClose={() => setIsEditModalOpen(false)}
          onSubmit={saveEditedGuarantor}
        />
      )}
    </>
  );
};

export default GuarantorVerification;
